import { useEffect, useMemo } from 'react';

import { MasterListEditor } from '../components/common';
import { useCategoryStore } from '../store';

import type { MasterListItem } from '../components/common/MasterListEditor';

const CategoryPage: React.FC = () => {
    const { categories, loading, fetchCategories, createCategory, updateCategory, deleteCategory } = useCategoryStore();

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    const items = useMemo(
        () =>
            categories.map((cat: any) => ({
                _id: cat._id,
                name: cat.name,
                code: cat.slug || cat._id,
                description: cat.description,
                parentId: cat.parentId,
                displayOrder: cat.displayOrder || 0,
                isActive: cat.status === 'active',
                metadata: {
                    questionCount: cat.questionCount || 0,
                    examCount: cat.examCount || 0,
                },
            })),
        [categories],
    );

    const handleSave = async (item: MasterListItem) => {
        if (item._id) {
            await updateCategory(item._id, {
                name: item.name,
                slug: item.code || undefined,
                description: item.description,
                parentId: item.parentId,
                displayOrder: item.displayOrder,
                status: item.isActive ? 'active' : 'archived',
            });
        } else {
            const slug = item.code || item.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
            await createCategory({
                name: item.name,
                slug,
                description: item.description,
                parentId: item.parentId,
                displayOrder: item.displayOrder,
            });
        }
    };

    const handleDelete = async (itemId: string) => {
        await deleteCategory(itemId);
    };

    const handleRefresh = async () => {
        await fetchCategories();
    };

    return (
        <div>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <MasterListEditor
                    title="Categories"
                    description="Organize your questions and exams with a hierarchical category system. Categories can be nested to create a tree structure."
                    items={items}
                    hierarchical={true}
                    onSave={handleSave}
                    onDelete={handleDelete}
                    onRefresh={handleRefresh}
                    loading={loading}
                    customFields={[
                        {
                            key: 'icon',
                            label: 'Icon',
                            type: 'icon',
                            required: false,
                        },
                        {
                            key: 'color',
                            label: 'Color',
                            type: 'color',
                            required: false,
                        },
                    ]}
                />

                <div className="mt-8 p-5 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-xl">
                    <h4 className="font-semibold text-teal-900 dark:text-teal-100 mb-2 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-teal-600 to-cyan-600 inline-block" />
                        Category Usage
                    </h4>
                    <ul className="text-sm text-teal-800 dark:text-teal-200 space-y-1.5 list-disc list-inside marker:text-teal-400">
                        <li>Categories help organize questions by topic, technology, or subject</li>
                        <li>Create hierarchies like: Technology → Programming → JavaScript</li>
                        <li>Use category codes in question filters and exam setup</li>
                        <li>Questions can belong to multiple categories</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default CategoryPage;
